import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Card, CardContent, CardMedia, CardActions, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  CircularProgress, Pagination
} from '@mui/material';
import { Add, Search, Edit, Delete, Visibility, DirectionsCar } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { productsAPI } from '../services/api';   // ← only productsAPI needed now
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const ProductCard = ({ product, onEdit, onDelete, onView }) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardMedia
        component="img"
        height="180"
        image={product.primary_image || `https://placehold.co/400x180/1a237e/white?text=${encodeURIComponent(product.brand_name||'Car')}`}
        alt={product.name}
        sx={{ objectFit: 'cover' }}
        onError={(e) => {
          e.target.src = `https://placehold.co/400x180/1a237e/white?text=${encodeURIComponent(product.brand_name||'Car')}`;
        }}
      />
    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {product.brand_name} {product.name}
        </Typography>
        <StatusChip status={product.status} />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {product.year} · {product.fuel_type || 'N/A'} · {product.transmission || 'N/A'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        SKU: {product.sku}
      </Typography>
      <Typography variant="h6" color="primary.main" fontWeight={700}>{fmt(product.selling_price)}</Typography>
      <Typography variant="caption" color="text.secondary">Stock: {product.total_stock || 0} units</Typography>
    </CardContent>
    <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
      <IconButton size="small" onClick={() => onView(product.id)} title="View"><Visibility fontSize="small" /></IconButton>
      <IconButton size="small" onClick={() => onEdit(product)} title="Edit"><Edit fontSize="small" /></IconButton>
      <IconButton size="small" color="error" onClick={() => onDelete(product.id)} title="Delete"><Delete fontSize="small" /></IconButton>
    </CardActions>
  </Card>
);

const ProductForm = ({ open, onClose, product, onSaved }) => {
  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const productType = watch('product_type', 'vehicle');

  useEffect(() => {
    if (open) {
      reset(product || { 
        condition: 'new', 
        status: 'available', 
        currency: 'USD', 
        product_type: 'vehicle' 
      });
      setImages([]);
      setImagePreviews([]);
    }
  }, [open, reset, product]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const onSubmit = async (data) => {
    const actionLabel = product?.id ? 'Product updated' : 'Product created';

    try {
      setLoading(true);
      let createdProduct;

      if (product?.id) {
        await productsAPI.update(product.id, data);
      } else {
        const response = await productsAPI.create(data);
        createdProduct = response.data.data || response.data;
      }
     
      if (images.length > 0 && (createdProduct?.id || product?.id)) {
        const formData = new FormData();
        images.forEach(img => formData.append('images', img));
        await productsAPI.uploadImages(createdProduct?.id || product.id, formData);
        toast.success(`${actionLabel} and images uploaded`);
      } else {
        toast.success(actionLabel);
      }
     
      onSaved();
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save product';

      if (images.length > 0) {
        toast.error(`${actionLabel}, but image upload failed: ${message}`);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{product?.id ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Product Type */}
            <Grid item xs={12}>
              <Controller
                name="product_type"
                control={control}
                defaultValue="vehicle"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Product Type</InputLabel>
                    <Select label="Product Type" {...field}>
                      <MenuItem value="vehicle">Vehicle (Car)</MenuItem>
                      {/* <MenuItem value="motorcycle">Motorcycle</MenuItem> */}
                      <MenuItem value="three_wheeler">Three-Wheeler (Bajaj)</MenuItem>
                      {/* <MenuItem value="spare_part">Spare Part</MenuItem> */}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {productType === 'spare_part' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Part Number" {...register('part_number')} />
              </Grid>
            )}

            {/* Other fields */}
            {[
              { name: 'name', label: 'Product Name', required: true, xs: 12, sm: 6 },
              { name: 'model', label: 'Model', required: true, xs: 12, sm: 6 },
              { name: 'year', label: 'Year', type: 'number', required: true, xs: 6, sm: 3 },
              { name: 'doors', label: 'Doors', type: 'number', xs: 6, sm: 3 },
              { name: 'cost_price', label: 'Cost Price', type: 'number', required: true, xs: 12, sm: 4 },
              { name: 'selling_price', label: 'Selling Price', type: 'number', required: true, xs: 12, sm: 4 },
              { name: 'min_selling_price', label: 'Min Selling Price', type: 'number', xs: 12, sm: 4 },
            ].map(({ name, label, required, type, xs, sm }) => (
              <Grid item xs={xs} sm={sm} key={name}>
                <TextField fullWidth label={label} type={type || 'text'}
                  {...register(name, required ? { required: `${label} is required` } : {})}
                  error={!!errors[name]} helperText={errors[name]?.message} />
              </Grid>
            ))}

            {/* Engine Type Dropdown */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="engine"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Engine Type</InputLabel>
                    <Select label="Engine Type" {...field}>
                      <MenuItem value="">Select Engine Type</MenuItem>
                      <MenuItem value="Electric">Electric</MenuItem>
                      <MenuItem value="Diesel/Petrol">Diesel/Petrol</MenuItem>
                      <MenuItem value="Hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Transmission Dropdown */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="transmission"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Transmission</InputLabel>
                    <Select label="Transmission" {...field}>
                      <MenuItem value="">Select Transmission</MenuItem>
                      <MenuItem value="Manual">Manual</MenuItem>
                      <MenuItem value="Automatic">Automatic</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {productType === 'spare_part' && (
              <Grid item xs={12}>
                <TextField fullWidth label="Compatible Models" {...register('compatible_models')} helperText="Comma-separated list of compatible models" />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={3} {...register('description')} />
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Product Images</Typography>
              <Button variant="outlined" component="label" startIcon={<Add />}>
                Upload Images
                <input type="file" hidden multiple accept="image/*" onChange={handleImageChange} />
              </Button>
              {imagePreviews.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {imagePreviews.map((preview, idx) => (
                    <img key={idx} src={preview} alt={`Preview ${idx + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }} />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (product?.id ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector(s => s.auth);
  const canEdit = user?.roles?.some(r => ['admin', 'manager', 'storekeeper'].includes(r));
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const LIMIT = 12;

  // Handle edit state from ProductDetailPage
  useEffect(() => {
    if (location.state?.editProduct) {
      setEditProduct(location.state.editProduct);
      setFormOpen(true);
      // Clear the state to prevent re-opening on navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await productsAPI.getAll({ page, limit: LIMIT, search: search || undefined, status: statusFilter || undefined });
      setProducts(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async () => {
    try {
      await productsAPI.delete(deleteId);
      toast.success('Product deleted');
      setDeleteId(null);
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Products</Typography>
          <Typography variant="body2" color="text.secondary">{total} vehicles in inventory</Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditProduct(null); setFormOpen(true); }}>
            Add Vehicle
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField placeholder="Search vehicles..." size="small" sx={{ minWidth: 280 }} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="sold">Sold</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={48} /></Box>
      ) : products.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <DirectionsCar sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No vehicles found</Typography>
          {canEdit && <Button variant="contained" sx={{ mt: 2 }} onClick={() => setFormOpen(true)}>Add First Vehicle</Button>}
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {products.map(p => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                <ProductCard product={p} onView={(id) => navigate(`/products/${id}`)} onEdit={(p) => { setEditProduct(p); setFormOpen(true); }} onDelete={(id) => setDeleteId(id)} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination count={Math.ceil(total / LIMIT)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </>
      )}

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} product={editProduct} onSaved={fetchProducts} />

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this vehicle? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
